/**
 * NewConversationModal — escolhe com quem iniciar uma conversa.
 *
 * Web (interno): 1:1 ou grupo (com nome). Aplicativo: conversa geral com um
 * prestador, sem estar atrelada a uma OS.
 */
import { useEffect, useMemo, useState } from "react";
import { Check, Loader2, Search, Smartphone, Users } from "lucide-react";
import api, { formatApiError } from "../../utils/api";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { toast } from "../ui/sonner";

export default function NewConversationModal({
  open,
  onOpenChange,
  mode,
  webUsers = [],
  mobileUsers = [],
  onCreated,
}) {
  const internal = mode === "internal";
  const [search, setSearch] = useState("");
  const [picked, setPicked] = useState([]);
  const [groupTitle, setGroupTitle] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      setSearch("");
      setPicked([]);
      setGroupTitle("");
    }
  }, [open, mode]);

  const list = internal ? webUsers : mobileUsers;
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? list.filter((u) => (u.name || "").toLowerCase().includes(q)) : list;
  }, [list, search]);

  const toggle = (id) =>
    setPicked((prev) =>
      internal
        ? prev.includes(id)
          ? prev.filter((x) => x !== id)
          : [...prev, id]
        : prev[0] === id
          ? []
          : [id],
    );

  async function submit() {
    if (!picked.length) return;
    setSaving(true);
    try {
      const { data } = internal
        ? await api.post("/chat/internal/threads", {
            participant_ids: picked,
            title: picked.length > 1 ? groupTitle.trim() || undefined : undefined,
          })
        : await api.post("/chat/threads/general", { mobile_user_id: picked[0] });
      onCreated?.(data.id);
      onOpenChange(false);
    } catch (err) {
      toast.error("Não foi possível abrir a conversa", {
        description: formatApiError(err.response?.data?.detail),
      });
    } finally {
      setSaving(false);
    }
  }

  const Icon = internal ? Users : Smartphone;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md !z-[1800]"
        overlayClassName="!z-[1799]"
        data-testid="chat-new-conversation-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon size={18} style={{ color: "var(--accent)" }} />
            Nova conversa
          </DialogTitle>
          <DialogDescription>
            {internal
              ? "Escolha uma pessoa para conversa direta, ou várias para criar um grupo."
              : "Escolha o prestador. A conversa é geral, sem vínculo com um serviço."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="relative">
            <Search
              size={14}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar pessoa…"
              className="pl-8 h-9 text-sm"
              data-testid="chat-new-search"
            />
          </div>

          <div className="max-h-64 overflow-y-auto rounded-lg border border-gray-200 divide-y divide-gray-100">
            {filtered.length === 0 ? (
              <p className="text-[12px] text-gray-400 px-3 py-6 text-center">
                {internal
                  ? "Nenhum outro usuário web neste tenant."
                  : "Nenhum prestador com aplicativo vinculado."}
              </p>
            ) : (
              filtered.map((u) => {
                const on = picked.includes(u.id);
                return (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => toggle(u.id)}
                    className={`w-full text-left px-3 py-2 text-[13px] flex items-center gap-2.5 transition-colors ${
                      on ? "bg-[var(--accent-light)]" : "hover:bg-gray-50"
                    }`}
                    data-testid={`chat-new-pick-${u.id}`}
                  >
                    <span
                      className="h-7 w-7 rounded-full text-[11px] font-semibold text-white flex items-center justify-center shrink-0"
                      style={{ backgroundColor: "var(--accent)" }}
                    >
                      {(u.name || "?").slice(0, 1).toUpperCase()}
                    </span>
                    <span className="flex-1 truncate text-gray-800">{u.name}</span>
                    {on && <Check size={15} style={{ color: "var(--accent)" }} />}
                  </button>
                );
              })
            )}
          </div>

          {internal && picked.length > 1 && (
            <Input
              value={groupTitle}
              onChange={(e) => setGroupTitle(e.target.value)}
              placeholder="Nome do grupo (ex: Plantão noturno)"
              className="h-9 text-sm"
              data-testid="chat-new-group-title"
            />
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="chat-new-cancel-btn"
          >
            Cancelar
          </Button>
          <Button
            onClick={submit}
            disabled={!picked.length || saving}
            className="text-white"
            style={{ backgroundColor: "var(--accent)" }}
            data-testid="chat-new-create-btn"
          >
            {saving && <Loader2 size={14} className="animate-spin mr-1.5" />}
            {internal && picked.length > 1 ? "Criar grupo" : "Abrir conversa"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
